import React, { useEffect, useState, useRef } from "react";
import { GoogleMap, Marker } from "@react-google-maps/api";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import PostalCodeSearch from "../components/common/PostalCodeSearch";
import { useNavigate, useSearchParams } from "react-router-dom";
import Slider from "react-slick";
import { useQuery } from "@tanstack/react-query";
import { getRequest } from "../services/apiMethods";
import { Button, Spinner } from "react-bootstrap";
import StorePopup from "../components/StorePopup";

// 🔁 Helper function moved to top
function getMapStyle() {
  return window.innerWidth <= 768
    ? { width: "100%", height: "100vh" }
    : { width: "100%", height: "600px" };
}

function createRoundIcon(imageUrl) {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  const size = 40;

  canvas.width = size;
  canvas.height = size;

  // Draw white circle background
  ctx.fillStyle = "white";
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, size / 2, 0, 2 * Math.PI);
  ctx.fill();

  // Draw border
  ctx.strokeStyle = "#ddd";
  ctx.lineWidth = 2;
  ctx.stroke();

  return canvas.toDataURL();
}

export default function ClubLocation() {
  const navigate = useNavigate(); // ✅ Only one navigate
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const [containerStyle, setContainerStyle] = useState(getMapStyle());
  const [selectedStoreId, setSelectedStoreId] = useState(null);
  const [selectedStore, setSelectedStore] = useState(null);
  const [center, setCenter] = useState(null);
  const [showNoStoresPopup, setShowNoStoresPopup] = useState(false);
  const [popupDismissed, setPopupDismissed] = useState(false);
  const mapRef = useRef(null);
  const clustererRef = useRef(null);

  const { data: getUserDetail, isLoading: loading } = useQuery({
    queryKey: ["getuserDetail", id],
    queryFn: () => getRequest(`user/detail/${id}`),
    enabled: Boolean(id),
    onSuccess: (data) => {
      const coordinates = data?.result?.userLocation?.coordinates;
      if (Array.isArray(coordinates) && coordinates.length === 2) {
        setCenter({ lat: coordinates[0], lng: coordinates[1] });
      }
    },
  });

  const { data: getStores, isLoading: isStoresLoading } = useQuery({
    queryKey: ["getStores", center],
    queryFn: () =>
      getRequest(`/store/localStores?lat=${center?.lat}&lng=${center?.lng}`),
    enabled: !!center,
  });

  useEffect(() => {
    const handleResize = () => setContainerStyle(getMapStyle());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const coordinates = getUserDetail?.result?.userLocation?.coordinates;
    if (Array.isArray(coordinates) && coordinates.length === 2) {
      setCenter({ lng: coordinates[0], lat: coordinates[1] });
    }
  }, [getUserDetail]);

  useEffect(() => {
    if (mapRef.current && getStores?.stores?.length > 0) {
      const createMarkers = async () => {
        const markers = await Promise.all(
          getStores.stores.map(async (club) => {
            const iconUrl = await new Promise((resolve) => {
              const canvas = document.createElement("canvas");
              const ctx = canvas.getContext("2d");
              const size = 40;

              canvas.width = size;
              canvas.height = size;

              const img = new Image();
              img.crossOrigin = "anonymous";

              img.onload = () => {
                // Draw white circle background
                ctx.fillStyle = "white";
                ctx.beginPath();
                ctx.arc(size / 2, size / 2, size / 2, 0, 2 * Math.PI);
                ctx.fill();

                // Draw border
                ctx.strokeStyle = "#e58d2e";
                ctx.lineWidth = 2;
                ctx.stroke();

                // Clip to circle for image
                ctx.save();
                ctx.beginPath();
                ctx.arc(size / 2, size / 2, size / 2 - 3, 0, 2 * Math.PI);
                ctx.clip();

                // Draw image
                ctx.drawImage(img, 3, 3, size - 6, size - 6);
                ctx.restore();

                resolve(canvas.toDataURL());
              };

              img.onerror = () => resolve(createRoundIcon());
              img.src =
                club?.logo || "https://i.imghippo.com/files/fhLU5977vQE.png";
            });

            const marker = new window.google.maps.Marker({
              position: {
                lat: club?.storeLocation?.coordinates[1],
                lng: club?.storeLocation?.coordinates[0],
              },
              icon: {
                url: iconUrl,
                scaledSize: new window.google.maps.Size(40, 40),
                anchor: new window.google.maps.Point(20, 20),
              },
            });

            marker.addListener("click", () => {
              sessionStorage.setItem("storeid", club?._id);
              setSelectedStore(club);
            });

            return marker;
          })
        );

        if (clustererRef.current) {
          clustererRef.current.clearMarkers();
        }
        clustererRef.current = new MarkerClusterer({
          map: mapRef.current,
          markers,
        });
      };

      createMarkers();
    }
  }, [getStores?.stores]);

  useEffect(() => {
    if (
      !isStoresLoading &&
      getStores &&
      (!getStores.stores || getStores.stores.length === 0) &&
      !popupDismissed
    ) {
      setShowNoStoresPopup(true);
    }
  }, [getStores, isStoresLoading, popupDismissed]);

  useEffect(() => {
    return () => {
      if (clustererRef.current) {
        clustererRef.current.clearMarkers();
      }
    };
  }, []);

  const handleLocationSelect = (newLocation) => {
    const newCenter = { lat: newLocation.lat, lng: newLocation.lng };
    setCenter(newCenter);
    setPopupDismissed(false);
    sessionStorage.setItem("mapCenter", JSON.stringify(newCenter));
    sessionStorage.setItem("postcode", newLocation?.postalCode);
  };

  const isSingleStore = getStores?.stores?.length === 1;

  const sliderSettings = {
    dots: false,
    arrows: false,
    infinite: !isSingleStore,
    speed: 500,
    slidesToShow: isSingleStore ? 1 : 1.5,
    slidesToScroll: 1,
    centerMode: !isSingleStore,
    centerPadding: isSingleStore ? "0px" : "20%",
    responsive: [
      {
        breakpoint: 768,
        settings: {
          slidesToShow: isSingleStore ? 1 : 1.2,
          centerMode: !isSingleStore,
          centerPadding: isSingleStore ? "0px" : "10%",
        },
      },
    ],
  };

  const handleSkip = () => {
    navigate(`/set-password`);
  };

  return (
    <section className="commn-bg-set">
      <div className="card-center-set">
        <div className="map-card-theme">
          {(loading || isStoresLoading) && (
            <div className="loading-overlay">
              <Spinner
                animation="border"
                role="status"
                style={{ color: "#e58d2e", width: "3rem", height: "3rem" }}
              />
            </div>
          )}

          {center && (
            <GoogleMap
              mapContainerStyle={containerStyle}
              center={center}
              zoom={13}
              onLoad={(map) => {
                mapRef.current = map;
              }}
            />
          )}

          <div className="map-search-set">
            <PostalCodeSearch
              onLocationSelect={handleLocationSelect}
              getUserDetail={getUserDetail}
            />
          </div>

          <div
            style={{
              position: "absolute",
              top: "105px",
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 1000,
              backgroundColor: "rgba(255, 255, 255, 0.95)",
              padding: "12px 24px",
              borderRadius: "25px",
              boxShadow: "rgba(0, 0, 0, 0.15) -1px 8px 12px 4px",
              border: "2px solid #e58d2e",
            }}
          >
            <h3
              style={{
                margin: 0,
                color: "#092c42",
                fontFamily: "Shadows Into Light",
                fontSize: "18px",
                fontWeight: "600",
                textAlign: "center",
              }}
            >
              Select Your Preferred Store
            </h3>
          </div>

          {/* <div className="coffee-side-shops" style={{ width: "100%", padding: "0 20px" }}>
            <Slider {...sliderSettings}>
              {Array.isArray(getStores?.stores) &&
                getStores.stores.map((ele, index) => (
                  <div
                    key={index}
                    className={`shop-item-set ${selectedStoreId === ele?._id ? "highlighted" : ""}`}
                    onClick={() => {
                      sessionStorage.setItem("storeid", ele?._id);
                      setSelectedStoreId(ele._id);
                      setSelectedStore(ele);
                    }}
                  >
                    <img src={require("../Assets/images/logo-main.png")} alt="store logo" />
                    <h3>{ele?.name}</h3>
                  </div>
                ))}


            </Slider>

          </div> */}
        </div>
        <div
          className="coffee-side-shops"
          style={{ width: "100%", padding: "0 20px" }}
        >
          <div className="skip-now-data">
            <Button variant="primary" type="button" onClick={handleSkip}>
              Skip For Now
            </Button>
          </div>
        </div>
      </div>

      {selectedStore && (
        <StorePopup
          store={selectedStore}
          onClose={() => setSelectedStore(null)}
          getStores={getStores}
        />
      )}

      {showNoStoresPopup && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-body text-center p-4">
                <h5>No Coffee Shops Available</h5>
                <p>
                  Currently there are no participating coffee shops in your
                  vicinity. New coffee shops are joining every day so please
                  check back regularly.
                </p>
                <Button
                  variant="primary"
                  onClick={() => {
                    setShowNoStoresPopup(false);
                    setPopupDismissed(true);
                  }}
                >
                  OK
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
